import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApiCaller } from '../../utils/ApiUtilities';
import {
    getIngestionDraftApproveUrl,
    getIngestionDraftByIdUrl,
    getIngestionSimilarByTextUrl,
} from '../../utils/UrlUtilities';
import {
    constructIngestionDraftPutPayload,
    constructIngestionSimilarByTextPayload,
} from '../../utils/PayloadUtils';
import {
    INGESTION_DEFAULT_TOP_K,
    SIMILARITY_THRESHOLDS,
} from '../../utils/Constants';
import { getMissingApprovalFields } from '../../utils/DraftValidation';
import ParsedDraftEditForm from './ParsedDraftEditForm';
import SimilarQuestionsPanel from './SimilarQuestionsPanel';
import SourceDocViewer from '../SourceDocViewer';
import QuestionPreviewModal from '../QuestionPreviewModal';
import CloseButton from '../ui/CloseButton';

// Split-view modal: left side is the edit form, right side is the similar-questions panel.
// Store = PUT (save edits) → POST approve (copy into questions_question). Cancel = close.
const ParsedDraftEditor = ({ draft: initialDraft, onClose, onApproved, onRejected, onNavigate, hasPrev = false, hasNext = false, serial = null, previewOpen = false, setPreviewOpen = null, sourceDoc = null, setSourceDoc = null }) => {
    const CallApi = useApiCaller();
    const [draft, setDraft] = useState(initialDraft);
    const [similar, setSimilar] = useState([]);
    const [similarLoading, setSimilarLoading] = useState(false);
    const [similarFetched, setSimilarFetched] = useState(false);
    const [storing, setStoring] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [localError, setLocalError] = useState('');
    // Preview open-state is lifted to the grid (so it survives this editor's keyed remount on
    // Next/Prev). Fall back to local state if a caller doesn't provide it.
    const [localPreview, setLocalPreview] = useState(false);
    const isPreviewOpen = setPreviewOpen ? previewOpen : localPreview;
    const openPreview = (v) => (setPreviewOpen || setLocalPreview)(v);
    // Unsaved-edit detection for Next/Prev navigation.
    const dirty = JSON.stringify(draft) !== JSON.stringify(initialDraft);

    // Map the flat snake_case draft to the camelCase `fields` QuestionPreviewModal expects.
    const previewFields = {
        questionText: draft.question_text || '',
        questionType: draft.question_type || 'MCQ',
        correctOptions: String(draft.correct_option || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean),
        options: { A: draft.option_a, B: draft.option_b, C: draft.option_c, D: draft.option_d, E: draft.option_e, F: draft.option_f, G: draft.option_g, H: draft.option_h },
        optionImages: { A: draft.option_a_image_url, B: draft.option_b_image_url, C: draft.option_c_image_url, D: draft.option_d_image_url, E: draft.option_e_image_url, F: draft.option_f_image_url, G: draft.option_g_image_url, H: draft.option_h_image_url },
        questionImageUrl: draft.question_image_url || '',
        blanks: (draft.metadata && draft.metadata.blanks) || [],
        textAnswer: draft.text_answer || '',
        sideText: draft.side_text || draft.passage_text || '',
        audioUrl: draft.audio_url || '',
        correctTextAnswer: draft.text_answer || '',
        answerExplanation: draft.answer_explanation || '',
        explanationImageUrl: draft.explanation_image_url || '',
    };

    // Preview Prev/Next: step to the neighbouring draft (unsaved-edit confirm first); when there's
    // no neighbour, just close the preview.
    const confirmDiscardIfDirty = () => !dirty || window.confirm('You have unsaved changes. Discard them and continue?');
    const previewPrev = () => { if (!hasPrev) { openPreview(false); return; } if (confirmDiscardIfDirty() && onNavigate) onNavigate(-1); };
    const previewNext = () => { if (!hasNext) { openPreview(false); return; } if (confirmDiscardIfDirty() && onNavigate) onNavigate(1); };


    // useApiCaller returns a fresh function each render; pin the latest via a
    // ref so the auto-fetch effect can run once on mount without re-firing.
    const callApiRef = useRef(CallApi);
    useEffect(() => { callApiRef.current = CallApi; });

    const fetchSimilarFor = useCallback(async (d) => {
        // Search against the given draft's values via POST /ingestion/similar-questions
        // — no PUT first, so opening the editor doesn't silently save the draft.
        if (!d || !d.level) {
            setSimilar([]);
            setSimilarFetched(false);
            return;
        }
        setSimilarLoading(true);
        setSimilarFetched(true);
        const excludeIds = d.approved_question_id ? [d.approved_question_id] : null;
        const res = await callApiRef.current({
            url: getIngestionSimilarByTextUrl(),
            method: 'POST',
            payload: constructIngestionSimilarByTextPayload(d, INGESTION_DEFAULT_TOP_K, excludeIds),
            token: true,
        });
        if (res.success) setSimilar(res.data.similar || []);
        setSimilarLoading(false);
    }, []);
    // "Re-fetch Relevant Questions" searches against the live (possibly edited) form values.
    const handleShowSimilar = useCallback(() => fetchSimilarFor(draft), [fetchSimilarFor, draft]);

    // On open AND when navigating to another draft (Next/Prev / Store & next), re-seed the local
    // state and re-fetch similar IN PLACE. The editor is no longer remounted (no key in the grid),
    // so the shared source-doc viewer keeps its scroll/zoom position across navigation.
    useEffect(() => {
        setDraft(initialDraft);
        setLocalError('');
        fetchSimilarFor(initialDraft);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDraft]);

    // navigateDir: 0 = close after storing; +1 = open next draft; -1 = open previous draft.
    // Re-storing an already-approved draft is allowed — the backend overrides the linked
    // question in place.
    const handleStore = async (navigateDir = 0) => {
        setLocalError('');
        // Defense in depth — the Store button is already disabled when missing
        // fields exist, but if a future change ever opens the gate, this stops
        // the request before it hits the server.
        if (missing.length > 0) {
            setLocalError(`Fill required field(s) before storing: ${missing.join(', ')}.`);
            return;
        }

        setStoring(true);
        // 1. Save the latest edits.
        const putRes = await CallApi({
            url: getIngestionDraftByIdUrl(draft.draft_id),
            method: 'PUT',
            payload: constructIngestionDraftPutPayload(draft),
            token: true,
        });
        if (!putRes.success) {
            setStoring(false);
            return;
        }
        // 2. Approve → copies into questions_question (or overrides it if already approved).
        const approveRes = await CallApi({
            url: getIngestionDraftApproveUrl(draft.draft_id),
            method: 'POST',
            token: true,
        });
        setStoring(false);
        if (approveRes.success) {
            // Pass the edited draft up so the grid row reflects the saved content (image, text,
            // …) — not just the status — otherwise reopening the row would show stale pre-edit data.
            onApproved && onApproved(draft.draft_id, approveRes.data.question_id, draft);
            if (navigateDir && onNavigate) onNavigate(navigateDir);
            else onClose();
        }
    };

    const handleReject = async () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm("Reject this draft? This can't be undone.")) return;
        setRejecting(true);
        const res = await CallApi({
            url: getIngestionDraftByIdUrl(draft.draft_id),
            method: 'DELETE',
            token: true,
        });
        setRejecting(false);
        if (res.success) {
            onRejected && onRejected(draft.draft_id);
            onClose();
        }
    };

    // Per-render check. Used to gate the Store button and surface an inline
    // hint listing the missing fields — matches the backend's _draft_ready_for_approve.
    const missing = getMissingApprovalFields(draft);

    const topSimilarity = similar.length > 0 ? similar[0].similarity_score : 0;
    const showDuplicateWarning = similarFetched && topSimilarity >= SIMILARITY_THRESHOLDS.DUPLICATE;

    return (
        <div
            className="modal d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                role="document"
                style={{ maxWidth: '1500px', width: '95vw' }}
            >
                <div className="modal-content kp-qform">
                    <div className="modal-header">
                        <div>
                            <h5 className="modal-title mb-1">Review Parsed Question — Draft #{draft.draft_id}</h5>
                            {(draft.source_type || draft.source_uri) && (
                                <div className="small" style={{ color: '#e9d5ff' }}>
                                    Source:{' '}
                                    {draft.source_type && (
                                        <span className="badge bg-light text-dark me-1">{draft.source_type}</span>
                                    )}
                                    <span title={draft.source_uri}>
                                        {(draft.source_uri || '').length > 70
                                            ? draft.source_uri.slice(0, 67) + '…'
                                            : draft.source_uri}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-sm"
                                style={{ background: 'linear-gradient(145deg, #8b5cf6, #6d28d9)', color: '#fff', border: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                                onClick={() => openPreview(true)}
                            >
                                👁 Preview Question In Test
                            </button>
                            <CloseButton onClick={onClose} />
                        </div>
                    </div>

                    <div className="modal-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'hidden' }}>
                        {showDuplicateWarning && (
                            <div className="alert alert-warning py-2 mb-3" role="alert">
                                <strong>⚠ Near-duplicate detected:</strong>{' '}
                                this question is {topSimilarity.toFixed(2)} similar to question #{similar[0].question_id}.
                                Review the right panel before storing.
                            </div>
                        )}
                        {missing.length > 0 && (
                            <div className="alert alert-warning py-2 mb-3" role="alert">
                                <strong>Required field(s) missing:</strong>{' '}
                                {missing.join(', ')}. Fill these to enable <em>Store</em>.
                            </div>
                        )}
                        {localError && (
                            <div className="alert alert-danger py-2 mb-3">{localError}</div>
                        )}

                        <div className="row g-3" style={{ height: 'calc(100vh - 320px)' }}>
                            <div className="col-md-7" style={{ overflowY: 'auto', maxHeight: '100%', overscrollBehavior: 'contain' }}>
                                <ParsedDraftEditForm draft={draft} onChange={setDraft} />

                                {/* Similar Existing Question — single top match, at the bottom of the details column. */}
                                <div className="mt-3 pt-3 border-top">
                                    <h6 className="mb-2">Similar Existing Question</h6>
                                    <SimilarQuestionsPanel
                                        loading={similarLoading}
                                        items={similar}
                                        hasFetched={similarFetched}
                                        currentQuestionId={draft.approved_question_id ?? null}
                                    />
                                </div>
                            </div>
                            <div
                                className="col-md-5 border-start ps-3 d-flex flex-column"
                                style={{ maxHeight: '100%', overflow: 'hidden' }}
                            >
                                {setSourceDoc && (
                                    <SourceDocViewer
                                        doc={sourceDoc}
                                        onDoc={setSourceDoc}
                                        onClose={() => setSourceDoc(null)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-outline-info me-auto"
                            onClick={handleShowSimilar}
                            disabled={similarLoading || storing || rejecting}
                        >
                            {similarLoading ? 'Loading…' : 'Re-fetch Relevant Questions'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={storing || rejecting}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={handleReject}
                            disabled={storing || rejecting}
                            title="Reject this draft (marks it REJECTED; it stays in the list)"
                        >
                            {rejecting ? 'Rejecting…' : 'Reject'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => handleStore(-1)}
                            disabled={storing || rejecting || missing.length > 0 || !hasPrev}
                            title={
                                !hasPrev
                                    ? 'No previous draft on this page'
                                    : 'Store this draft, then open the previous one'
                            }
                        >
                            ◀ Store &amp; show previous
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleStore(0)}
                            disabled={storing || rejecting || missing.length > 0}
                            title={
                                missing.length > 0
                                    ? `Cannot store — missing: ${missing.join(', ')}`
                                    : 'Save this draft into the live question bank'
                            }
                        >
                            {storing ? 'Storing…' : 'Store'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleStore(1)}
                            disabled={storing || rejecting || missing.length > 0 || !hasNext}
                            title={
                                !hasNext
                                    ? 'No next draft on this page'
                                    : 'Store this draft, then open the next one'
                            }
                        >
                            Store &amp; show next ▶
                        </button>
                    </div>
                </div>
            </div>
            <QuestionPreviewModal
                open={isPreviewOpen}
                onClose={() => openPreview(false)}
                fields={previewFields}
                serialLabel={serial != null ? `Draft #${serial}` : ''}
                onPrev={previewPrev}
                onNext={previewNext}
                sourceDoc={sourceDoc}
                setSourceDoc={setSourceDoc}
            />
        </div>
    );
};

export default ParsedDraftEditor;
